import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Brain, AudioLines, PenTool, BookOpen, Briefcase, Heart } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const ventures = [
  {
    id: 'lifecapsule-ai',
    name: 'LifeCapsule AI',
    tagline: 'Preserve Your Story. Forever.',
    icon: Brain,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/rvzeh285_LCAI_LMS_Logo_Prototype_8.jpg',
    mission: 'To help individuals capture, preserve, and share their life stories using artificial intelligence—ensuring personal legacies endure for generations.',
    problem: 'Personal stories, family histories, and individual wisdom are lost every day. Traditional methods of preservation—journals, photo albums, oral histories—are fragmented and easily lost. The richness of a human life deserves better.',
    solution: 'LifeCapsule AI uses advanced AI to conduct interactive interviews, capture stories in natural conversation, organize memories intelligently, and create enduring digital legacies that can be shared with family and future generations. Meet Elsie—your Digital Legacy Preservation Assistant.',
    innovation: 'AI-powered conversational capture, intelligent story organization, multi-generational sharing platform',
    role: 'Founder & Lead Architect',
    color: '#3B82F6',
    externalUrl: 'https://www.LifeCapsule.net',
    email: 'info@LifeCapsule.net'
  },
  {
    id: 'newaudia',
    name: 'NewAudia',
    tagline: 'The Next Chapter in Audio',
    icon: AudioLines,
    image: '/assets/newaudia_logo_rect.png',
    mission: 'To revolutionize how audio content is created, distributed, and experienced through innovative technology platforms.',
    problem: 'Audio content creation remains technically challenging and distribution fragmented. Podcasters, musicians, and audio creators struggle with complex tools and limited reach.',
    solution: 'NewAudia provides an integrated platform for audio creation, enhancement, and distribution—simplifying the technical aspects while amplifying creative potential.',
    innovation: 'AI-enhanced audio processing, streamlined distribution, creator-first platform design',
    role: 'Co-Founder & Strategist',
    color: '#8B5CF6',
    externalUrl: 'https://www.NewAudia.com',
    email: 'info@NewAudia.com'
  },
  {
    id: 'digital-ink-ai',
    name: 'Digital Ink AI',
    tagline: 'Where Words Meet Intelligence',
    icon: PenTool,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/n4rbd88f_DIGITAL_Ink_AI_Logo_Gold.jpg',
    mission: 'To bridge traditional publishing with artificial intelligence capabilities, empowering authors and publishers with intelligent tools.',
    problem: 'The publishing industry faces pressure from both AI advancement and changing reader expectations. Traditional workflows struggle to incorporate new technologies while maintaining quality.',
    solution: 'Digital Ink AI provides intelligent publishing tools that assist authors in writing, editing, and production—augmenting human creativity with AI capabilities.',
    innovation: 'AI-assisted editing, intelligent formatting, human-AI collaborative writing tools',
    role: 'Founder & Technology Lead',
    color: '#10B981',
    externalUrl: 'https://www.DigitalInkAI.com',
    email: 'info@DigitalInkAI.com'
  },
  {
    id: 'the-dash-story',
    name: 'The Dash Story',
    tagline: 'Because a Life is More Than a Dash Between Dates',
    icon: Heart,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/gv1l7nv0_The_Dash_by_LifeCapsule_Left.jpg',
    mission: 'To help families preserve and honor their loved ones\' stories through custom-written eulogies, beautifully bound legacy books, and interactive memorial webpages.',
    problem: 'The dash on a gravestone represents so little... but behind it lives a lifetime of love, sacrifice, laughter, service, and story. Your loved one\'s story deserves to be remembered—not reduced to a dash.',
    solution: 'The Dash Story by LifeCapsule offers compassionate services: Tribute Speeches ($350), Dash Story Legacy Books ($1,250), Website Memorials ($195), and complete Legacy Packages ($1,500). Fast turnaround, available nationwide.',
    innovation: 'Custom-written eulogies, heirloom quality bound books, interactive memorial webpages, AI-assisted story capture',
    role: 'Founder',
    color: '#EC4899',
    externalUrl: 'https://www.TheDashStory.com',
    email: 'info@TheDashStory.com'
  },
  {
    id: 'use-what-you-got-publishing',
    name: 'Use What You Got Publishing',
    tagline: 'People Are Waiting To Hear Your Story',
    icon: BookOpen,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/btrrvjk1_UWYG_Publishing_Self_Publishing_Services_Header_Logo.jpg',
    mission: 'To democratize publishing by providing accessible tools, knowledge, and support for independent authors and creators.',
    problem: 'Aspiring authors face significant barriers to publishing—complex processes, high costs, and lack of industry knowledge. Traditional publishing gatekeepers exclude many worthy voices.',
    solution: 'Use What You Got Publishing provides comprehensive support for independent authors—from manuscript development to marketing—based on the philosophy of maximizing available resources.',
    innovation: 'Practical publishing frameworks, resource optimization strategies, entrepreneurial author development',
    role: 'Founder & Publisher',
    color: '#F59E0B',
    facebook: 'https://www.facebook.com/profile.php?id=61565378824392'
  },
  {
    id: 'ai-means-business',
    name: 'AI Means Business',
    tagline: 'Practical AI Guidance for Real Businesses',
    icon: Briefcase,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/lb24raw7_A_I_Means_Business_R_Dale_Brunet.jpg',
    mission: 'To help businesses understand and leverage artificial intelligence strategically—moving beyond hype to practical implementation. Decision clarity before commitment.',
    problem: 'Businesses struggle to separate AI hype from reality. Many organizations either over-invest in flashy solutions that don\'t deliver or under-invest and fall behind competitors.',
    solution: 'AI Means Business provides strategic consulting and education that helps organizations understand AI\'s real potential, identify practical applications, and implement solutions that deliver measurable results. We don\'t sell tools—we provide structured analysis, context, and decision frameworks.',
    innovation: 'Practical AI strategy frameworks, business-focused implementation guidance, ROI-driven approach',
    role: 'Principal Consultant',
    color: '#EF4444',
    externalUrl: 'https://www.AIMeansBusiness.net',
    email: 'info@AIMeansBusiness.net'
  }
];

export default function VenturesPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl">
          <motion.span
            className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Ventures
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="ventures-headline"
          >
            Building Real Things
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Each venture addresses a specific challenge at the intersection of technology, creativity, and human potential. Explore the initiatives that define this work.
          </motion.p>
        </div>
      </Section>

      {/* Ventures Detail */}
      {ventures.map((venture, index) => (
        <Section key={venture.id} dark={index % 2 === 1} id={venture.id}>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${venture.color}20` }}
                >
                  <venture.icon className="w-6 h-6" style={{ color: venture.color }} />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">{venture.name}</h2>
                  <p className="text-muted-foreground">{venture.tagline}</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Mission</h3>
                  <p className="text-slate-300 leading-relaxed">{venture.mission}</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">The Problem</h3>
                  <p className="text-muted-foreground leading-relaxed">{venture.problem}</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">The Solution</h3>
                  <p className="text-muted-foreground leading-relaxed">{venture.solution}</p>
                </div>

                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Innovation: </span>
                    <span className="text-slate-300">{venture.innovation}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Role: </span>
                    <span className="text-slate-300">{venture.role}</span>
                  </div>
                </div>

                {(venture.externalUrl || venture.facebook) && (
                  <div className="flex flex-wrap gap-3 pt-4">
                    {venture.externalUrl && (
                      <a
                        href={venture.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-medium"
                        data-testid={`visit-${venture.id}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Visit Website
                      </a>
                    )}
                    {venture.facebook && (
                      <a
                        href={venture.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors text-sm font-medium"
                        data-testid={`facebook-${venture.id}`}
                      >
                        Follow on Facebook
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-2xl overflow-hidden border border-border bg-slate-900">
                <img
                  src={venture.image}
                  alt={venture.name}
                  className="w-full aspect-video object-contain p-4"
                />
              </div>
            </motion.div>
          </div>
        </Section>
      ))}

      {/* CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Interested in Collaboration?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Whether you're exploring partnership opportunities, seeking strategic advice, or interested in learning more about these ventures, let's connect.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="discuss-ventures-btn">
              <Link to="/contact">Discuss Opportunities</Link>
            </Button>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="schedule-call-btn">
              <Link to="/contact">Schedule a Call</Link>
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
